// library/LibraryTable — the Library as a conventional TABLE (owner, D23
// 2026-10-06): a header row naming the columns, a click on a header sorts by
// it (again: the other direction, shown with an arrow + `aria-sort`), one row
// per resource. Free text adds a heading row per matched field and the
// content hits after, each hit with its snippet on a line below.
//
// The search field controls it (a combobox): the arrow keys in the field move
// the marked row (`aria-activedescendant`), and a row itself takes Enter /
// Space (go in) and ↑ ↓ (move the mark) once it has focus. Pure projection:
// every interaction reports up.

import type { KeyboardEvent, ReactNode } from "react";
import { AiMarker } from "../../components/AiMarker/AiMarker.js";
import {
    encodeResourceDrag,
    RESOURCE_DRAG_MIME,
} from "../../components/Composer/PowerComposer/resourceDrag.js";
import type { ComposerResourceKind } from "../../components/Composer/PowerComposer/types.js";
import { Icon, type IconName } from "../../components/Icon/Icon.js";
import type { ContentSection } from "../../components/Search/contentSearch.js";
import type {
    CompactRow,
    LibraryResults,
    LibrarySort,
    SearchResult,
    SearchResultKind,
    SortColumn,
    TextSegment,
} from "../../components/Search/searchModel.js";
import { SourceReadStatus } from "../../components/SourceReadStatus/SourceReadStatus.js";
import type { LibraryLabels } from "./labels.js";

const KIND_ICON: Record<SearchResultKind, IconName> = {
    source: "source",
    material: "material",
    package: "export",
    file: "file",
    conversation: "chat",
};

// A row's type icon (C-01): a quiz reads as a checklist; anything else by
// its kind.
const rowIcon = (result: SearchResult): IconName =>
    result.facts?.type === "material" && result.facts.template === "quiz"
        ? "mcq"
        : KIND_ICON[result.kind];

// The columns shown (redesign C-01: the title, what it is, when it was last
// edited), plus the actions column. Sorting is the toolbar's sort menu.
const SHOWN_COLUMNS: readonly SortColumn[] = ["title", "kind", "edited"];
const COLUMN_COUNT = SHOWN_COLUMNS.length + 1;

const Highlighted = ({
    segments,
}: {
    readonly segments: readonly TextSegment[];
}): ReactNode => {
    let offset = 0;
    return (
        <>
            {segments.map((segment) => {
                const key = `${offset}:${segment.text}`;
                offset += segment.text.length;
                return segment.matched ? (
                    <mark key={key} className="bsx-mark">
                        {segment.text}
                    </mark>
                ) : (
                    <span key={key}>{segment.text}</span>
                );
            })}
        </>
    );
};

// The composer's mention kind for a result, or null when it can't be mentioned.
const composerKindOf = (
    kind: SearchResultKind,
): ComposerResourceKind | null => {
    switch (kind) {
        case "source":
        case "material":
        case "package":
            return kind;
        // A file isn't evidence until promoted, so it can't ground a message.
        case "file":
        case "conversation":
            return null;
    }
};

// Keeps a cell's inner controls' clicks and keys to themselves: without this
// fence a click would also open the row, and Enter on a button (or in the
// rename field) would also go in.
const EventFence = ({ children }: { readonly children: ReactNode }) => (
    // biome-ignore lint/a11y/noStaticElementInteractions: an event fence, not a control; the controls inside carry the semantics
    <span
        className="bsx-fence"
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
    >
        {children}
    </span>
);

export type RowKey = "up" | "down";

export type LibraryTableProps = {
    readonly ranked: LibraryResults;
    readonly content: ContentSection;
    readonly sort: LibrarySort;
    readonly labels: LibraryLabels;
    // The free-text group headings ("Title · 2", "In the content").
    readonly groupHeading: (key: string, count: number) => string;
    readonly contentNote: (section: ContentSection) => string | undefined;
    readonly markedId: string | null;
    readonly openedId: string | null;
    readonly rowId: (id: string) => string;
    readonly onOpen: (result: SearchResult, page?: number) => void;
    readonly onRowKey: (key: RowKey) => void;
    readonly trailing: (result: SearchResult) => ReactNode;
    readonly renameField: (result: SearchResult) => ReactNode;
    readonly pageLabel: (page: number) => string;
    readonly ariaLabel: string;
};

export const LibraryTable = (props: LibraryTableProps): ReactNode => {
    const { ranked, content, sort, labels } = props;
    // The column names stay for assistive tech (a table needs headers);
    // the sighted list reads without a header row, as in the design.
    const header = (column: SortColumn): ReactNode => (
        <th
            key={column}
            scope="col"
            className={`bsx-th bsx-col-${column}`}
            aria-sort={sort.column === column ? sort.direction : "none"}
        >
            <span className="bsx-th-name">{labels.column(column)}</span>
        </th>
    );
    const heading = (key: string, text: string): ReactNode => (
        <tr key={`h-${key}`} className="bsx-group-row">
            <th scope="colgroup" colSpan={COLUMN_COUNT}>
                {text}
            </th>
        </tr>
    );
    const note = props.contentNote(content);
    return (
        <table
            id="bsx-results"
            aria-label={props.ariaLabel}
            className="bsx-table"
        >
            <thead className="bsx-thead">
                <tr>
                    {SHOWN_COLUMNS.map(header)}
                    <td className="bsx-col-actions" />
                </tr>
            </thead>
            {ranked.groups.map((group) => (
                <tbody key={group.key}>
                    {ranked.axis === "field" ||
                    (ranked.axis === "kind" && group.key === "sources")
                        ? heading(
                              group.key,
                              props.groupHeading(group.key, group.rows.length),
                          )
                        : null}
                    {group.rows.map((row) => (
                        <Row key={row.result.id} row={row} props={props} />
                    ))}
                </tbody>
            ))}
            {content.type === "hidden" ? null : (
                <tbody>
                    {heading(
                        "content",
                        props.groupHeading(
                            "content",
                            content.type === "rows" ? content.rows.length : 0,
                        ),
                    )}
                    {content.type === "rows"
                        ? content.rows.flatMap((hit) => [
                              <Row
                                  key={hit.result.id}
                                  row={{
                                      result: hit.result,
                                      titleSegments: [
                                          {
                                              text: hit.result.title,
                                              matched: false,
                                          },
                                      ],
                                  }}
                                  page={hit.hit.page}
                                  props={props}
                              />,
                              <tr
                                  key={`s-${hit.result.id}`}
                                  className="bsx-snippet-row"
                              >
                                  <td colSpan={COLUMN_COUNT}>
                                      {hit.hit.page !== undefined
                                          ? `${props.pageLabel(hit.hit.page)}: `
                                          : null}
                                      <Highlighted
                                          segments={hit.snippetSegments}
                                      />
                                  </td>
                              </tr>,
                          ])
                        : null}
                    {note !== undefined ? (
                        <tr className="bsx-note-row">
                            <td colSpan={COLUMN_COUNT}>{note}</td>
                        </tr>
                    ) : null}
                </tbody>
            )}
        </table>
    );
};

const Row = ({
    row,
    page,
    props,
}: {
    readonly row: CompactRow;
    readonly page?: number;
    readonly props: LibraryTableProps;
}): ReactNode => {
    const { result } = row;
    const { labels } = props;
    const selected = result.id === props.markedId;
    const rename = props.renameField(result);
    const onKeyDown = (event: KeyboardEvent<HTMLTableRowElement>) => {
        if (event.metaKey || event.ctrlKey || event.altKey) return;
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            props.onOpen(result, page);
        } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            props.onRowKey(event.key === "ArrowDown" ? "down" : "up");
        }
    };
    // A mentionable row (not a conversation) drags onto a composer as an
    // `@`-mention — the typed resource payload, parsed back at the drop.
    const mentionable = composerKindOf(result.kind);
    // Relative when it is recent ("2 h ago"), else the date; the exact date
    // is one hover away.
    const edited = labels.recency(result.recency);
    const exact = labels.date(result.editedAt);
    // What it is, then what it was made from (a material's sources, a
    // package's material).
    const info = [labels.type(result), labels.basedOn(result)]
        .filter((part) => part !== "")
        .join(" · ");
    return (
        <tr
            id={props.rowId(result.id)}
            tabIndex={-1}
            aria-selected={selected}
            aria-current={result.id === props.openedId ? "true" : undefined}
            className={`bsx-row bilda-row-overflow-host${selected ? " bsx-row--selected" : ""}${result.id === props.openedId ? " bsx-row--opened" : ""}${result.archived === true ? " bsx-row--archived" : ""}`}
            onClick={() => props.onOpen(result, page)}
            onKeyDown={onKeyDown}
            draggable={mentionable !== null && rename === undefined}
            onDragStart={(event) => {
                if (mentionable === null) return;
                event.dataTransfer.setData(
                    RESOURCE_DRAG_MIME,
                    encodeResourceDrag({
                        kind: mentionable,
                        id: result.id,
                        title: result.title,
                    }),
                );
                event.dataTransfer.effectAllowed = "copy";
            }}
        >
            <td className="bsx-col-title" title={result.title}>
                <span className="bsx-cell-title">
                    <span className="bsx-row-icon" aria-hidden="true">
                        <Icon name={rowIcon(result)} size="sm" />
                    </span>
                    {rename !== undefined ? (
                        <EventFence>{rename}</EventFence>
                    ) : (
                        <span className="bsx-row-title">
                            <Highlighted segments={row.titleSegments} />
                        </span>
                    )}
                    {result.aiAuthored === true ? (
                        <AiMarker variant="tag" />
                    ) : null}
                </span>
                <SourceReadStatus status={result.readStatus} />
            </td>
            <td className="bsx-col-kind" title={info}>
                {info}
            </td>
            <td className="bsx-col-edited">
                {result.editedAt !== undefined ? (
                    <time dateTime={result.editedAt} title={exact}>
                        {edited === "" ? exact : edited}
                    </time>
                ) : (
                    edited
                )}
            </td>
            <td className="bsx-col-actions">
                <EventFence>{props.trailing(result)}</EventFence>
            </td>
        </tr>
    );
};
